import { useEffect, useRef, useState } from "react";
import { home } from "../content/home";
import { useContent, prefersReducedMotion } from "../ui/helpers";

/*
  "Why KSS" — the statement on the left, an animated signal-flow card on the right:
  stitch / trim / stop signals travel from the machine into K SMART V2 and build
  the production picture (bars + counters). Sample values only.
  The animation runs only while the card is on screen, can be paused, and stays
  still for visitors who prefer reduced motion.
*/
const LANES = [
  "M112 62 C160 62 168 150 196 150",
  "M112 150 L196 150",
  "M112 238 C160 238 168 150 196 150",
];
const OUT = "M264 150 L304 150";
const BARS = [0.72, 0.9, 0.55, 0.82, 0.66];

function SignalFlow({ v, playing }) {
  const svg = useRef(null);
  const [n, setN] = useState({ signals: 12480, bundles: 52 });

  useEffect(() => {
    const el = svg.current;
    if (!el || !el.pauseAnimations) return;
    if (playing) el.unpauseAnimations(); else el.pauseAnimations();
  }, [playing]);

  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => setN((p) => {
      const signals = p.signals + 1 + Math.floor(Math.random() * 3);
      return { signals, bundles: 52 + Math.floor((signals - 12480) / 48) };
    }), 160);
    return () => clearInterval(t);
  }, [playing]);

  const fmt = (x) => x.toLocaleString("en-US");
  return (
    <div className={`why-viz${playing ? "" : " hold"}`}>
      <div className="why-viz-top"><span>{v.label}</span><span className="led" aria-hidden="true" /></div>
      <svg ref={svg} viewBox="0 0 460 300" aria-hidden="true" focusable="false">
        {LANES.map((d, i) => <path key={d} d={d} className="lane" style={{ animationDelay: `${i * 0.3}s` }} />)}
        <path d={OUT} className="lane out" />
        {v.lanes.map((name, i) => (
          <g key={name} className="src" transform={`translate(14 ${44 + i * 88})`}>
            <rect width="98" height="36" rx="10" />
            <circle cx="16" cy="18" r="4" className={`src-led l${i}`} />
            <text x="28" y="22">{name}</text>
          </g>
        ))}
        {LANES.map((d, i) => [0, 1].map((k) => (
          <circle key={`${i}-${k}`} r="4.5" className={`dot d${i}`} visibility="hidden">
            <set attributeName="visibility" to="visible" begin={`${0.1 + i * 0.45 + k * 0.9}s`} />
            <animateMotion dur="1.8s" begin={`${0.1 + i * 0.45 + k * 0.9}s`} repeatCount="indefinite" path={d} />
          </circle>
        )))}
        {[0, 1, 2].map((k) => (
          <circle key={`o${k}`} r="4" className="dot dout" visibility="hidden">
            <set attributeName="visibility" to="visible" begin={`${0.9 + k * 0.3}s`} />
            <animateMotion dur="0.9s" begin={`${0.9 + k * 0.3}s`} repeatCount="indefinite" path={OUT} />
          </circle>
        ))}
        <g className="hub">
          <circle cx="230" cy="150" r="46" className="hub-ring" />
          <circle cx="230" cy="150" r="34" className="hub-core" />
          <g transform="translate(213 128) scale(0.1)">
            <line x1="155" y1="145" x2="155" y2="445" stroke="#F6F8F7" strokeWidth="76" strokeLinecap="round" transform="translate(-100 -80)" />
            <g transform="translate(-100 -80)">
              <line x1="192" y1="295" x2="387" y2="145" stroke="#16E6B2" strokeWidth="62" />
              <circle cx="387" cy="145" r="52" fill="#16E6B2" /><circle cx="387" cy="145" r="23" fill="#0B2536" />
              <line x1="192" y1="295" x2="387" y2="445" stroke="#1EC8FF" strokeWidth="70" />
              <circle cx="192" cy="295" r="52" fill="#1EC8FF" /><circle cx="192" cy="295" r="23" fill="#0B2536" />
              <circle cx="387" cy="445" r="52" fill="#1EC8FF" /><circle cx="387" cy="445" r="23" fill="#0B2536" />
            </g>
          </g>
          <text x="230" y="214" className="hub-label">{v.hub}</text>
        </g>
        <g className="pic" transform="translate(304 66)">
          <rect width="142" height="168" rx="14" className="pic-card" />
          <text x="14" y="26" className="pic-title">{v.picture}</text>
          <line x1="14" y1="142" x2="128" y2="142" className="pic-base" />
          {BARS.map((h, i) => {
            const H = h * 92, lo = H * 0.3, k = "0;0.45;0.8;1", begin = `${0.15 + i * 0.18}s`;
            return (
              <rect key={i} x={18 + i * 22} y={142 - H} width="14" height={H} rx="3" className={`bar${i === 2 ? " low" : ""}`}>
                <animate attributeName="height" values={`${lo};${H};${H};${lo}`} keyTimes={k} dur="3.6s" begin={begin} repeatCount="indefinite" />
                <animate attributeName="y" values={`${142 - lo};${142 - H};${142 - H};${142 - lo}`} keyTimes={k} dur="3.6s" begin={begin} repeatCount="indefinite" />
              </rect>
            );
          })}
          <text x="14" y="160" className="pic-axis">{v.axis}</text>
        </g>
      </svg>
      <dl className="why-stats">
        <div><dt>{v.stats[0]}</dt><dd>{fmt(n.signals)}</dd></div>
        <div><dt>{v.stats[1]}</dt><dd>{n.bundles}</dd></div>
        <div><dt>{v.stats[2]}</dt><dd className="ok">0</dd></div>
      </dl>
    </div>
  );
}

export function Statement() {
  const c = useContent(home).statement;
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  const [hold, setHold] = useState(prefersReducedMotion);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setSeen(e.isIntersecting), { threshold: 0.25 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  // The statement is one sentence split into pieces. Undo the "keep the last two words
  // together" rule inside the pieces, so lines fill naturally; only the very end keeps it.
  const mid = (t) => String(t).replace(/ /g, " ");
  return (
    <section className="band-light statement" ref={ref} aria-labelledby="why-title">
      <div className="k-wrap">
        <div className="why-grid">
          <div className="why-copy rv">
            <p className="k-label" id="why-title">{c.eyebrow}</p>
            <p className="big">
              {mid(c.lead)} <span className="dim">{mid(c.dim)}</span> {mid(c.before)} <span className="signal">{mid(c.mark)}</span>{c.after}
            </p>
          </div>
          <figure className="why-fig rv">
            <SignalFlow v={c.viz} playing={seen && !hold} />
            <figcaption>
              <span>{c.viz.note}</span>
              <button type="button" className="tbtn" onClick={() => setHold((h) => !h)} aria-pressed={hold}>
                {hold ? `▶ ${c.viz.play}` : `❚❚ ${c.viz.pause}`}
              </button>
            </figcaption>
          </figure>
        </div>
        <div className="statement-meta rv">
          <p>{c.body}</p>
          <div>
            <p className="sig">{c.signature}</p>
            <div className="stitch" aria-hidden="true" />
          </div>
        </div>
      </div>
    </section>
  );
}
